import { ref } from 'vue';

export type ToastSeverity = 'success' | 'warn' | 'critical';

export interface ToastEntry {
    id: number;
    message: string;
    severity: ToastSeverity;
    duration: number | null;
}

const DEFAULT_TOAST_DURATION = 4000;
const toasts = ref<ToastEntry[]>([]);
let nextToastId = 1;
const toastTimeouts = new Map<number, number>();

const clearToastTimeout = (id: number) => {
    const timeoutId = toastTimeouts.get(id);
    if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        toastTimeouts.delete(id);
    }
};

const scheduleToastDismiss = (id: number, duration: number | null) => {
    clearToastTimeout(id);

    if (duration === null) {
        return;
    }

    const timeoutId = window.setTimeout(() => dismissToast(id), duration);
    toastTimeouts.set(id, timeoutId);
};

const dismissToast = (id: number) => {
    clearToastTimeout(id);
    toasts.value = toasts.value.filter(toast => toast.id !== id);
};

const showToast = (
    message: string,
    severity?: ToastSeverity,
    duration: number | null = DEFAULT_TOAST_DURATION
) => {
    const id = nextToastId++;
    const toast: ToastEntry = {
        id,
        message,
        severity: severity ?? 'critical',
        duration,
    };

    toasts.value.push(toast);
    scheduleToastDismiss(id, toast.duration);

    return id;
};

const updateToast = (
    id: number,
    updates: Partial<Pick<ToastEntry, 'message' | 'severity' | 'duration'>>
) => {
    const toast = toasts.value.find(entry => entry.id === id);
    if (!toast) {
        return;
    }

    if (updates.message !== undefined) {
        toast.message = updates.message;
    }

    if (updates.severity !== undefined) {
        toast.severity = updates.severity;
    }

    if (updates.duration !== undefined) {
        toast.duration = updates.duration;
    } else {
        toast.duration = DEFAULT_TOAST_DURATION;
    }

    scheduleToastDismiss(id, toast.duration);
};

const clearToasts = () => {
    toastTimeouts.forEach((_, id) => clearToastTimeout(id));
    toasts.value = [];
};

export function useToast() {
    return {
        toasts,
        showToast,
        updateToast,
        dismissToast,
        clearToasts,
    };
}
